import { useState, useEffect, useMemo } from 'react'
import { useDepartment } from '../../context/DepartmentContext'
import { useToast } from '../../context/ToastContext'
import { getCategories, UNIT_LABELS } from '../../data/categories'
import { getProducts } from '../../data/products'
import { getProductPrice } from '../../lib/productPricing'
import { getDistributionPoints } from '../../data/bakeryData'
import { getYawehsDistributionPoints } from '../../data/yawehsData'
import { SHIFTS } from '../../data/bakeryData'
import { YAWEHS_SHIFTS } from '../../data/yawehsData'
import {
  saveMorningLog, getMorningLogs,
  saveSaleTransaction, getSalesTransactions,
  saveProductionBatch, getProductionLogs,
  saveDistributionBatch, getDistributionLogs,
} from '../../lib/store'
import ArithmeticInput from '../ui/ArithmeticInput'
import { Save, Plus, PlusCircle, Package, Truck, CheckCircle } from 'lucide-react'

// Kitchen: Add to Morning Log
function AddToMorningLog({ onSaved }) {
  const { currentDepartmentId: dept } = useDepartment()
  const { addToast } = useToast()
  const today = new Date().toISOString().split('T')[0]
  const categories = getCategories()

  const existing = useMemo(() => getMorningLogs(dept, today), [dept])
  const existingMap = useMemo(() => {
    const m = {}
    for (const e of existing) m[e.category] = e.value
    return m
  }, [existing])

  const [values, setValues] = useState({})

  const handleSave = () => {
    const entries = Object.entries(values).filter(([, v]) => v !== '' && Number(v) > 0)
    if (entries.length === 0) { addToast('Enter at least one value', 'warning'); return }

    for (const [catName, value] of entries) {
      const cat = categories.find(c => c.name === catName)
      const existingVal = existingMap[catName] || 0
      const newTotal = existingVal + Number(value)
      saveMorningLog(dept, today, catName, newTotal, cat?.openingUnit || 'units')
    }

    addToast(`Added ${entries.length} items to morning log`, 'success')
    setValues({})
    onSaved?.()
  }

  return (
    <div className="space-y-4">
      <div className="text-xs text-gray-400 mb-2">
        Adding to morning log for <span className="text-white font-medium">{today}</span>.
        Quantities entered below will be <span className="text-green-400">added</span> to existing totals.
      </div>

      {existing.length > 0 && (
        <div className="bg-gray-800/50 rounded-lg p-3">
          <p className="text-xs text-gray-400 mb-2 font-medium">Current totals:</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {existing.map(e => (
              <div key={e.category} className="flex items-center gap-2 text-xs">
                <CheckCircle size={10} className="text-green-400 shrink-0" />
                <span className="capitalize text-gray-300">{e.category}:</span>
                <span className="text-white font-medium">{e.value} {UNIT_LABELS[e.unit]}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-1 max-h-[50vh] overflow-y-auto">
        {categories.map(cat => (
          <div key={cat.name} className="flex items-center gap-3 bg-gray-800/50 rounded px-4 py-1.5">
            <span className="w-36 text-sm capitalize">{cat.label}</span>
            <span className="text-gray-500 text-xs w-12">{UNIT_LABELS[cat.openingUnit]}</span>
            {existingMap[cat.name] > 0 && (
              <span className="text-xs text-gray-500 w-16">({existingMap[cat.name]})</span>
            )}
            <ArithmeticInput
              min="0"
              value={values[cat.name] ?? ''}
              onChange={(val) => setValues(prev => ({ ...prev, [cat.name]: val == null ? '' : val }))}
              className="bg-gray-900 border border-gray-700 rounded px-3 py-1 text-sm text-white w-24"
              placeholder="+0"
            />
          </div>
        ))}
      </div>

      <button
        onClick={handleSave}
        className="flex items-center gap-2 bg-brand-600 hover:bg-brand-500 text-white px-6 py-2.5 rounded-lg text-sm font-medium"
      >
        <Plus size={16} /> Add to Morning Log
      </button>
    </div>
  )
}

// Bakery/Yahwehs: Add to Production Log
function AddToProduction({ onSaved }) {
  const { currentDepartmentId: dept } = useDepartment()
  const { addToast } = useToast()
  const isYawehs = dept === 'yawehs'
  const today = new Date().toISOString().split('T')[0]
  const products = getProducts(dept)
  const shifts = isYawehs ? YAWEHS_SHIFTS : SHIFTS
  const [shift, setShift] = useState('day')
  const [values, setValues] = useState({})

  const existing = useMemo(() => getProductionLogs(today, null, dept), [dept])
  const existingByProduct = useMemo(() => {
    const m = {}
    for (const e of existing) {
      m[e.productCode] = (m[e.productCode] || 0) + (Number(e.qtyProduced) || 0)
    }
    return m
  }, [existing])

  const handleSave = () => {
    const entries = Object.entries(values)
      .filter(([, v]) => v !== '' && Number(v) > 0)
      .map(([productCode, qtyProduced]) => ({ productCode, qtyProduced: Number(qtyProduced) }))

    if (entries.length === 0) { addToast('Enter at least one quantity', 'warning'); return }

    // Add to existing: load current shift data, merge
    const currentShift = getProductionLogs(today, shift, dept)
    const currentMap = {}
    for (const c of currentShift) currentMap[c.productCode] = Number(c.qtyProduced) || 0

    const merged = entries.map(e => ({
      productCode: e.productCode,
      qtyProduced: (currentMap[e.productCode] || 0) + e.qtyProduced,
    }))

    saveProductionBatch(today, shift, merged, dept)
    addToast(`Added ${entries.length} items to ${shift} shift production`, 'success')
    setValues({})
    onSaved?.()
  }

  return (
    <div className="space-y-4">
      <div className="text-xs text-gray-400 mb-2">
        Adding to production log for <span className="text-white font-medium">{today}</span>.
        Quantities will be <span className="text-green-400">added</span> to existing totals.
      </div>

      <div className="flex gap-1">
        {shifts.map(s => (
          <button
            key={s.value}
            onClick={() => setShift(s.value)}
            className={`px-4 py-2 rounded text-sm font-medium ${
              shift === s.value ? 'bg-brand-700 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {Object.keys(existingByProduct).length > 0 && (
        <div className="bg-gray-800/50 rounded-lg p-3">
          <p className="text-xs text-gray-400 mb-2 font-medium">Today's production totals (all shifts):</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {Object.entries(existingByProduct).map(([code, qty]) => {
              const p = products.find(pr => pr.code === code)
              return (
                <div key={code} className="flex items-center gap-2 text-xs">
                  <CheckCircle size={10} className="text-green-400 shrink-0" />
                  <span className="text-gray-300">{p?.name || code}:</span>
                  <span className="text-white font-medium">{qty}</span>
                </div>
              )
            })}
          </div>
        </div>
      )}

      <div className="space-y-1 max-h-[50vh] overflow-y-auto">
        {products.map(p => (
          <div key={p.code} className="flex items-center gap-3 bg-gray-800/50 rounded px-4 py-1.5">
            <span className="w-10 font-mono text-xs text-gray-500">{p.code}</span>
            <span className="w-40 text-sm">{p.name}</span>
            {existingByProduct[p.code] > 0 && (
              <span className="text-xs text-gray-500 w-12">({existingByProduct[p.code]})</span>
            )}
            <ArithmeticInput
              min="0"
              value={values[p.code] ?? ''}
              onChange={(val) => setValues(prev => ({ ...prev, [p.code]: val == null ? '' : val }))}
              className="bg-gray-900 border border-gray-700 rounded px-3 py-1 text-sm text-white w-24"
              placeholder="+0"
            />
          </div>
        ))}
      </div>

      <button
        onClick={handleSave}
        className="flex items-center gap-2 bg-brand-600 hover:bg-brand-500 text-white px-6 py-2.5 rounded-lg text-sm font-medium"
      >
        <Plus size={16} /> Add to Production
      </button>
    </div>
  )
}

// Bakery/Yahwehs: Add to Distribution
function AddToDistribution({ onSaved }) {
  const { currentDepartmentId: dept } = useDepartment()
  const { addToast } = useToast()
  const isYawehs = dept === 'yawehs'
  const today = new Date().toISOString().split('T')[0]
  const products = getProducts(dept)
  const points = isYawehs ? getYawehsDistributionPoints() : getDistributionPoints()
  const [pointId, setPointId] = useState('')
  const [values, setValues] = useState({})

  const existing = useMemo(
    () => pointId ? getDistributionLogs(today, pointId, dept) : [],
    [dept, pointId]
  )
  const existingMap = useMemo(() => {
    const m = {}
    for (const e of existing) m[e.productCode] = { sent: e.qtySent, sold: e.qtySold }
    return m
  }, [existing])

  const handleSave = () => {
    if (!pointId) { addToast('Select a distribution point', 'warning'); return }

    const entries = Object.entries(values)
      .filter(([, v]) => v && (v.sent || v.sold))
      .map(([productCode, v]) => {
        const ex = existingMap[productCode] || { sent: 0, sold: 0 }
        const newSent = (Number(ex.sent) || 0) + (Number(v.sent) || 0)
        const newSold = (Number(ex.sold) || 0) + (Number(v.sold) || 0)
        const p = products.find(pr => pr.code === productCode)
        return {
          productCode,
          qtySent: newSent,
          qtySold: newSold,
          salesRands: newSold * (getProductPrice(p, dept) || 0),
        }
      })

    if (entries.length === 0) { addToast('Enter at least one value', 'warning'); return }

    saveDistributionBatch(today, pointId, entries, dept)
    const pointName = points.find(p => p.id === pointId)?.name || pointId
    addToast(`Added ${entries.length} items to ${pointName}`, 'success')
    setValues({})
    onSaved?.()
  }

  return (
    <div className="space-y-4">
      <div className="text-xs text-gray-400 mb-2">
        Adding to distribution for <span className="text-white font-medium">{today}</span>.
        Quantities will be <span className="text-green-400">added</span> to existing totals.
      </div>

      <div>
        <label className="block text-xs text-gray-400 mb-1">Distribution Point</label>
        <select
          value={pointId}
          onChange={e => setPointId(e.target.value)}
          className="bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white min-w-[200px]"
        >
          <option value="">Select point...</option>
          {points.map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      </div>

      {pointId && (
        <>
          {existing.length > 0 && (
            <div className="bg-gray-800/50 rounded-lg p-3">
              <p className="text-xs text-gray-400 mb-2 font-medium">Current entries for this point:</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {existing.map(e => {
                  const p = products.find(pr => pr.code === e.productCode)
                  return (
                    <div key={e.productCode} className="text-xs">
                      <span className="text-gray-300">{p?.name || e.productCode}:</span>
                      <span className="text-white font-medium ml-1">Sent {e.qtySent}, Sold {e.qtySold}</span>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          <div className="space-y-1 max-h-[50vh] overflow-y-auto">
            {/* Header */}
            <div className="grid text-gray-400 text-xs font-medium border-b border-gray-800 py-2"
              style={{ gridTemplateColumns: '56px 1fr 88px 88px' }}>
              <div className="px-2">Code</div>
              <div className="px-2">Product</div>
              <div className="px-2 text-right">+ Sent</div>
              <div className="px-2 text-right">+ Sold</div>
            </div>
            {/* Rows */}
            {products.map(p => (
              <div key={p.code} className="grid items-center border-b border-gray-800/50 py-1"
                style={{ gridTemplateColumns: '56px 1fr 88px 88px' }}>
                <div className="px-2 font-mono text-xs text-gray-500">{p.code}</div>
                <div className="px-2 text-sm truncate">{p.name}</div>
                <div className="px-2">
                  <ArithmeticInput
                    min="0"
                    value={values[p.code]?.sent ?? ''}
                    onChange={(val) => setValues(prev => ({
                      ...prev, [p.code]: { ...prev[p.code], sent: val == null ? '' : val }
                    }))}
                    className="w-full bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm text-white text-right"
                    placeholder="+0"
                  />
                </div>
                <div className="px-2">
                  <ArithmeticInput
                    min="0"
                    value={values[p.code]?.sold ?? ''}
                    onChange={(val) => setValues(prev => ({
                      ...prev, [p.code]: { ...prev[p.code], sold: val == null ? '' : val }
                    }))}
                    className="w-full bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm text-white text-right"
                    placeholder="+0"
                  />
                </div>
              </div>
            ))}
          </div>

          <button
            onClick={handleSave}
            className="flex items-center gap-2 bg-brand-600 hover:bg-brand-500 text-white px-6 py-2.5 rounded-lg text-sm font-medium"
          >
            <Plus size={16} /> Add to Distribution
          </button>
        </>
      )}
    </div>
  )
}

export default function AddToLog({ onSaved }) {
  const { currentDepartmentId: dept } = useDepartment()
  const isBakery = dept === 'bakery' || dept === 'yawehs'

  const tabs = isBakery
    ? [
        { id: 'production', label: 'Production Log', icon: Package },
        { id: 'distribution', label: 'Distribution', icon: Truck },
      ]
    : [
        { id: 'morning', label: 'Morning Log', icon: Package },
      ]

  const [tab, setTab] = useState(tabs[0].id)

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <PlusCircle size={18} className="text-brand-400" />
        <h3 className="text-sm font-medium text-white">Add to Today's Log</h3>
      </div>

      {tabs.length > 1 && (
        <div className="flex gap-1">
          {tabs.map(t => {
            const Icon = t.icon
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded text-sm font-medium ${
                  tab === t.id ? 'bg-brand-700 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'
                }`}
              >
                <Icon size={14} /> {t.label}
              </button>
            )
          })}
        </div>
      )}

      {tab === 'morning' && <AddToMorningLog onSaved={onSaved} />}
      {tab === 'production' && <AddToProduction onSaved={onSaved} />}
      {tab === 'distribution' && <AddToDistribution onSaved={onSaved} />}
    </div>
  )
}
